import { offlineStorage } from './offlineStorage';
import toast from 'react-hot-toast';

interface SyncStatus {
  isSyncing: boolean;
  lastSync: number | null;
  pendingItems: number;
  error: string | null;
}

class SyncManager {
  private status: SyncStatus = {
    isSyncing: false,
    lastSync: null,
    pendingItems: 0,
    error: null,
  };

  private listeners: Set<(status: SyncStatus) => void> = new Set();

  constructor() {
    this.loadStatus();
    this.setupOnlineListener();
  }

  private loadStatus() {
    const saved = localStorage.getItem('syncStatus');
    if (saved) {
      this.status = JSON.parse(saved);
    }
  }

  private saveStatus() {
    localStorage.setItem('syncStatus', JSON.stringify(this.status));
    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener({ ...this.status }));
  }

  private setupOnlineListener() {
    window.addEventListener('online', () => {
      console.log('Network restored, triggering sync...');
      this.sync();
    });

    window.addEventListener('offline', () => {
      console.log('Network lost');
      this.status.error = 'Offline';
      this.saveStatus();
    });
  }

  subscribe(listener: (status: SyncStatus) => void) {
    this.listeners.add(listener);
    listener({ ...this.status });
    return () => this.listeners.delete(listener);
  }

  getStatus(): SyncStatus {
    return { ...this.status };
  }

  async sync(): Promise<void> {
    if (this.status.isSyncing) {
      console.log('Sync already in progress');
      return;
    }

    const isOnline = navigator.onLine;
    if (!isOnline) {
      this.status.error = 'No internet connection';
      this.saveStatus();
      toast.error('Cannot sync: No internet connection');
      return;
    }

    this.status.isSyncing = true;
    this.status.error = null;
    this.saveStatus();
    toast.loading('Syncing data...', { id: 'sync-toast' });

    try {
      const queue = await offlineStorage.getSyncQueue();
      this.status.pendingItems = queue.length;
      this.saveStatus();

      // Process sync queue
      for (const item of queue) {
        try {
          await this.processQueueItem(item);
          await offlineStorage.removeFromSyncQueue(item.id);
        } catch (error) {
          console.error('Failed to sync item:', item, error);
          await offlineStorage.incrementSyncQueueRetries(item.id);
          
          // Remove items that have failed too many times
          if (item.retries >= 3) {
            await offlineStorage.removeFromSyncQueue(item.id);
            toast.error(`Failed to sync after 3 retries: ${item.endpoint}`);
          }
        }
      }

      // Download latest data from server
      await this.downloadFromServer();

      this.status.lastSync = Date.now();
      this.status.pendingItems = 0;
      this.saveStatus();
      toast.success('Sync completed successfully', { id: 'sync-toast' });
    } catch (error) {
      console.error('Sync failed:', error);
      this.status.error = error instanceof Error ? error.message : 'Sync failed';
      this.saveStatus();
      toast.error('Sync failed: ' + (error instanceof Error ? error.message : 'Unknown error'), { id: 'sync-toast' });
    } finally {
      this.status.isSyncing = false;
      this.saveStatus();
    }
  }

  private async processQueueItem(item: any): Promise<void> {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    let response: Response;

    switch (item.method) {
      case 'POST':
        response = await fetch(`/api${item.endpoint}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(item.data),
        });
        break;
      case 'PUT':
        response = await fetch(`/api${item.endpoint}/${item.data._id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(item.data),
        });
        break;
      case 'DELETE':
        response = await fetch(`/api${item.endpoint}/${item.data._id}`, {
          method: 'DELETE',
          headers,
        });
        break;
      default:
        throw new Error(`Unknown method: ${item.method}`);
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Update local record sync status
    if (item.endpoint.includes('/orders')) {
      await offlineStorage.updateOrderSyncStatus(item.data._id, 'synced');
    } else if (item.endpoint.includes('/customers')) {
      await offlineStorage.updateCustomerSyncStatus(item.data._id, 'synced');
    }
  }

  private async downloadFromServer(): Promise<void> {
    const token = localStorage.getItem('token');
    const headers = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    // Download services
    try {
      const servicesRes = await fetch('/api/services', { headers });
      if (servicesRes.ok) {
        const services = await servicesRes.json();
        await offlineStorage.saveServices(Array.isArray(services) ? services : services.data || []);
      }
    } catch (error) {
      console.error('Failed to sync services:', error);
    }

    // Download inventory
    try {
      const inventoryRes = await fetch('/api/inventory', { headers });
      if (inventoryRes.ok) {
        const inventory = await inventoryRes.json();
        await offlineStorage.saveInventory(Array.isArray(inventory) ? inventory : inventory.data || []);
      }
    } catch (error) {
      console.error('Failed to sync inventory:', error);
    }
  }

  async queueOperation(method: 'POST' | 'PUT' | 'DELETE', endpoint: string, data: any) {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    await offlineStorage.addToSyncQueue({ id, method, endpoint, data });
    
    // Also save to local storage
    if (endpoint.includes('/orders')) {
      await offlineStorage.saveOrder(data);
    } else if (endpoint.includes('/customers')) {
      await offlineStorage.saveCustomer(data);
    }

    this.status.pendingItems++;
    this.saveStatus();

    // Try to sync immediately if online
    if (navigator.onLine) {
      this.sync();
    }
  }
}

export const syncManager = new SyncManager();
